import { useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetLmsCreateRequestsQuery, useAddLmsCreateRequestMutation, useUpdateLmsCreateRequestMutation, useDeleteLmsCreateRequestMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';

const BACKEND_COLUMN_MAP = {
    vehicleModel: 'VehicleModel',
    trainingType: 'TrainingType',
    numberOfParticipants: 'NumberOfParticipants',
    preferTrainingDate: 'PreferTrainingDate',
    alternativeDate: 'AlternativeDate',
    location: 'LocationCode',
    trainingLanguage: 'TrainingLanguage',
    status: 'Status',
    createdDate: 'CreatedDate',
    createdName: 'CreatedName'
};

// Tablodaki (TrainingRequestTable.jsx) kolon filtresi gruplarıyla birebir aynı — kesin eşleşme
// (dropdown) / metin (huni + eşleşme modu menüsü) / tarih (tek Calendar, DATE_IS) alanları.
const EXACT_FILTER_FIELDS = ['vehicleModel', 'trainingType', 'location'];
const TEXT_FILTER_FIELDS = ['numberOfParticipants', 'trainingLanguage', 'status', 'createdName'];
const DATE_FILTER_FIELDS = ['preferTrainingDate', 'alternativeDate', 'createdDate'];

const INITIAL_FILTER_STATE = {
    vehicleModel: null,
    trainingType: null,
    location: null,
    numberOfParticipants: null,
    numberOfParticipantsMatchMode: null,
    trainingLanguage: null,
    trainingLanguageMatchMode: null,
    status: null,
    statusMatchMode: null,
    createdName: null,
    createdNameMatchMode: null,
    preferTrainingDate: null,
    alternativeDate: null,
    createdDate: null
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];
    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;
        const column = BACKEND_COLUMN_MAP[field];
        if (!column) return;

        let filterValue;
        if (DATE_FILTER_FIELDS.includes(field) && value instanceof Date) {
            filterValue = value.toISOString();
        } else {
            filterValue = String(value).trim();
            if (!filterValue) return;
        }

        filters.push({ column, operator: matchModeToOperator[matchMode] ?? matchModeToOperator[FilterMatchMode.EQUALS], value: filterValue });
    };

    EXACT_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], FilterMatchMode.EQUALS));
    TEXT_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], filtersState?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS));
    DATE_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], FilterMatchMode.DATE_IS));

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || !order) return [{ column: 'CreatedDate', direction: 2 }];
    const column = BACKEND_COLUMN_MAP[field] || field;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

/**
 * Eğitim Talebi (LmsCreateRequest) ekranı için CRUD veri/state hook'u. Bkz. docs/lms-create-request.md.
 *
 * Filtreleme, projede "statik kolon filtresi" olarak bilinen desende — PrimeReact
 * `DataTable filterDisplay="row"` + kolon başlığı içi inline filtreler — tablodaki TÜM
 * kolonlara uygulanıyor (bkz. warranty-claims/hook/useWarrantyClaimsList.js referans deseni).
 * Tek fark: burada alan sayısı fazla olduğu için her alanı ayrı ayrı yazmak yerine
 * EXACT/TEXT/DATE_FILTER_FIELDS grupları üzerinden döngüyle üretiliyor (aynı davranış, daha az
 * tekrar) — `TrainingRequestTable.jsx`'teki `tableFilters`/`handleTableFilter` de aynı grupları kullanır.
 */
const useTrainingRequestData = (requestType) => {
    const { th } = useLanguage();
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    // `requestType` sekme seçimiyle sabitlenen bir filtre — kullanıcı arayüzünden değiştirilemez,
    // bu yüzden `activeFilters`/kolon filtreleri sisteminin dışında, doğrudan burada ekleniyor.
    // `RequestType` kolonunun `GetList`'in `filters` dizisinde diğer kolonlarla aynı şekilde
    // filtrelenebildiği BE tarafından doğrulandı — { column: 'RequestType', operator: 5, value }
    // formatı test edildi (bkz. "docs/LMS EĞİTİM TALEBİ_new-changed.txt" madde 6).
    const queryParams = useMemo(
        () => ({
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            filters: requestType != null ? [...buildDynamicFilters(activeFilters), { column: 'RequestType', operator: matchModeToOperator[FilterMatchMode.EQUALS], value: String(requestType) }] : buildDynamicFilters(activeFilters),
            sorts: buildDynamicSorts(sortField, sortOrder)
        }),
        [lazyParams, activeFilters, sortField, sortOrder, requestType]
    );

    const { data: trainingRequestApi, isLoading, isFetching, error, refetch } = useGetLmsCreateRequestsQuery(queryParams, { refetchOnMountOrArgChange: true });

    const [addMutation] = useAddLmsCreateRequestMutation();
    const [updateMutation] = useUpdateLmsCreateRequestMutation();
    const [deleteMutation] = useDeleteLmsCreateRequestMutation();

    const saveRequest = async (formData) => {
        try {
            if (formData.id) {
                await updateMutation(formData).unwrap();
            } else {
                await addMutation(formData).unwrap();
            }
            toastService.success(th('trainingRequest.saveSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('trainingRequest.saveError'), th('common.error'));
            return false;
        }
    };

    const deleteRequest = async (id) => {
        await deleteMutation(id).unwrap();
        refetch?.();
    };

    const onPage = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            page: event.page + 1,
            first: event.first,
            rows: event.rows
        }));
    };

    const onFiltersChange = (updated) => {
        setActiveFilters(updated);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const onSortChange = (field, order) => {
        setSortField(field);
        setSortOrder(order);
    };

    return {
        trainingRequestApi: trainingRequestApi || { items: [], totalCount: 0 },
        isLoading,
        isFetching,
        error,
        lazyParams,
        onPage,
        activeFilters,
        onFiltersChange,
        initFilters,
        sortField,
        sortOrder,
        onSortChange,
        saveRequest,
        deleteRequest,
        refetch
    };
};

export default useTrainingRequestData;
